import {
    Counterpart,
    counterpartOf,
    IdentifiedRoi,
    RoiCode,
} from '~/domain/comparison/roiCounterpart';
import { RoiInterpretedType } from '~/domain/dicom/dicomstructureset';
import type { ContourAssignment } from '~/state/atoms/adaptiveReplanningAtoms';

const EXCLUDED_BY_DEFAULT: ReadonlySet<string> = new Set([
    RoiInterpretedType.EXTERNAL,
    RoiInterpretedType.SUPPORT,
    RoiInterpretedType.CONTROL,
    RoiInterpretedType.PTV,
]);

export interface SourceRoi {
    readonly name: string;
    readonly interpretedType?: string;
    readonly code?: RoiCode;
}

export type RoiSource = 'propagate' | 'contour';

export interface RoiPlanEntry {
    readonly name: string;
    readonly included: boolean;
    readonly source: RoiSource;
    // The model structure that fills this one, found automatically; absent when none matches.
    readonly modelRoiName?: string;
    readonly contourable: boolean;
    // A pairing by meaning alone: Contour+ only once the user confirms it.
    readonly needsConfirmation: boolean;
}

export type RoiPlan = Record<
    string,
    {
        included: boolean;
        source: RoiSource;
        // The model structure the user confirmed for a suggested pairing.
        confirmedModelRoiName?: string;
    }
>;

// A suggested pairing starts propagated; the user confirms it before Contour+ replaces anything.
function defaultSource(counterpart: Counterpart | undefined): RoiSource {
    return counterpart && counterpart.match !== 'suggested'
        ? 'contour'
        : 'propagate';
}

// Judged by the ROI's interpreted type, never its name: naming differs by site and system.
function isExcludedByDefault(roi: SourceRoi): boolean {
    return EXCLUDED_BY_DEFAULT.has((roi.interpretedType ?? '').toUpperCase());
}

export function defaultRoiPlan(
    sourceRois: readonly SourceRoi[],
    modelRois: readonly IdentifiedRoi[]
): RoiPlan {
    return Object.fromEntries(
        sourceRois.map((roi) => [
            roi.name,
            {
                included: !isExcludedByDefault(roi),
                source: defaultSource(counterpartOf(roi, modelRois)),
            },
        ])
    );
}

export function roiPlanEntries(
    sourceRois: readonly SourceRoi[],
    modelRois: readonly IdentifiedRoi[],
    plan: RoiPlan
): RoiPlanEntry[] {
    return [...sourceRois]
        .sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
        )
        .map((roi) => {
            const entry = plan[roi.name];
            const counterpart = counterpartOf(roi, modelRois);
            const needsConfirmation = counterpart?.match === 'suggested';
            // A structure the model does not produce can only be propagated;
            // a suggestion is contoured only for the model structure the user confirmed.
            const source: RoiSource = !counterpart
                ? 'propagate'
                : needsConfirmation
                  ? entry?.source === 'contour' &&
                    entry.confirmedModelRoiName === counterpart.name
                      ? 'contour'
                      : 'propagate'
                  : (entry?.source ?? defaultSource(counterpart));
            return {
                name: roi.name,
                included: entry?.included ?? !isExcludedByDefault(roi),
                source,
                modelRoiName: counterpart?.name,
                contourable: !!counterpart,
                needsConfirmation,
            };
        });
}

// Every listed structure is propagated: it stands wherever Contour+ produces nothing.
export function structuresToTransform(
    entries: readonly RoiPlanEntry[]
): string[] {
    return entries.filter((entry) => entry.included).map((entry) => entry.name);
}

export function structuresToContour(
    entries: readonly RoiPlanEntry[]
): ContourAssignment[] {
    return entries.flatMap((entry) =>
        entry.included && entry.source === 'contour' && entry.modelRoiName
            ? [{ name: entry.name, modelRoiName: entry.modelRoiName }]
            : []
    );
}
